import React, { createContext, useContext, useState, useEffect } from 'react'

interface AccessibilityContextType {
  reducedMotion: boolean
  highContrast: boolean
  focusVisible: boolean
  setReducedMotion: (value: boolean) => void
  setHighContrast: (value: boolean) => void
  setFocusVisible: (value: boolean) => void
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined)

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext)
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider')
  }
  return context
}

interface AccessibilityProviderProps {
  children: React.ReactNode
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [reducedMotion, setReducedMotion] = useState(false)
  const [highContrast, setHighContrast] = useState(false)
  const [focusVisible, setFocusVisible] = useState(false)

  useEffect(() => {
    // Check for user's motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mediaQuery.matches)
    
    const handleChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handleChange)
    
    // Check for high contrast preference
    const contrastQuery = window.matchMedia('(prefers-contrast: high)')
    setHighContrast(contrastQuery.matches)
    
    const handleContrastChange = (e: MediaQueryListEvent) => setHighContrast(e.matches)
    contrastQuery.addEventListener('change', handleContrastChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
      contrastQuery.removeEventListener('change', handleContrastChange)
    }
  }, [])

  useEffect(() => {
    // Apply accessibility classes to body
    document.body.classList.toggle('reduced-motion', reducedMotion)
    document.body.classList.toggle('high-contrast', highContrast)
    document.body.classList.toggle('focus-visible', focusVisible)
  }, [reducedMotion, highContrast, focusVisible])

  return (
    <AccessibilityContext.Provider
      value={{
        reducedMotion,
        highContrast,
        focusVisible,
        setReducedMotion,
        setHighContrast,
        setFocusVisible
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  )
}